import type { ReactNode } from 'react';

import { useAuth, usePortalSession } from '../auth';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdministrator?: boolean;
}

export function ProtectedRoute({
  children,
  requireAdministrator = false,
}: ProtectedRouteProps) {
  const { isLoading } = useAuth();
  const portalSession = usePortalSession();

  if (isLoading || portalSession.status === 'loading') {
    return (
      <SessionMessage
        eyebrow="Authentication"
        title="Loading your portal profile"
        description="Please wait while the portal verifies your access."
      />
    );
  }

  if (portalSession.status === 'signedOut') {
    return (
      <SessionMessage
        eyebrow="Authentication required"
        title="Sign in to continue"
        description="This page is available to authenticated portal users."
        actionLabel="Sign in"
        onAction={() => window.location.assign('/')}
      />
    );
  }

  if (portalSession.status === 'forbidden') {
    return (
      <SessionMessage
        eyebrow="Access denied"
        title="Your portal account cannot access this page"
        description={
          portalSession.message ??
          'Contact a portal administrator if you need access.'
        }
        actionLabel="Return to sign in"
        onAction={() => window.location.assign('/')}
      />
    );
  }

  if (portalSession.status === 'error') {
    return (
      <SessionMessage
        eyebrow="Portal unavailable"
        title="Your portal profile could not be loaded"
        description={
          portalSession.message ??
          'Try loading the profile again.'
        }
        actionLabel="Try again"
        onAction={portalSession.refresh}
      />
    );
  }

  if (
    requireAdministrator &&
    !portalSession.user?.isAdministrator
  ) {
    return (
      <SessionMessage
        eyebrow="Access denied"
        title="Administrator access is required"
        description="Your identity is valid, but this page is restricted to portal administrators."
        actionLabel="Return to portal"
        onAction={() => window.location.assign('/portal')}
      />
    );
  }

  return children;
}

interface SessionMessageProps {
  eyebrow: string;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

function SessionMessage({
  eyebrow,
  title,
  description,
  actionLabel,
  onAction,
}: SessionMessageProps) {
  return (
    <section className="session-message-page">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p className="hero-description">{description}</p>

      {actionLabel && onAction ? (
        <button
          className="primary-button"
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      ) : null}
    </section>
  );
}
